import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import * as Sentry from '@sentry/browser';
import { RewriteFrames } from '@sentry/integrations';
import { Integrations as TracingIntegrations } from '@sentry/tracing';
import { TransactionContext } from '@sentry/types';

import { AccountBase, SentryConfig } from './config';
import { RtLocation } from './location.service';

@Injectable()
export class RtcErrorHandler implements ErrorHandler {
  private readonly filteredStatuses: number[] = [0, 401, 403, 404];
  private readonly appRevision: string = this.injector.get(this.sentryConfig.revisionToken);
  private nextPath: string;
  private dialogActive = false;

  constructor(
    private account: AccountBase,
    private sentryConfig: SentryConfig,
    private injector: Injector,
    private route: ActivatedRoute,
    private router: Router,
    private location: RtLocation,
  ) {
    if (!this.sentryConfig.enable) {
      return;
    }

    this.location.go$.subscribe(path => (this.nextPath = path));

    Sentry.init({
      dsn: this.sentryConfig.dsn,
      release: this.appRevision,
      whitelistUrls: [
        /https:\/\/(dxouce9bndnyz|d2m5u00bfeg39x)\.cloudfront\.net\/(.*).js/,
        /https?:\/\/(.+\.)?recruitee\.com\/(.*)\/(.*)\.js/,
        /https?:\/\/app.recruitee.local:3003\/(.*)\.js/,
      ],
      integrations: [
        new RewriteFrames({
          iteratee: frame => {
            if (frame.filename) {
              frame.in_app = /(.*)(runtime|main|common|\d+)\.(.*)\.js/.test(frame.filename);
            }
            return frame;
          },
        }),
        new TracingIntegrations.BrowserTracing({
          beforeNavigate: (context: TransactionContext): TransactionContext => ({
            ...context,
            ...this.getTransactionName(context),
          }),
        }),
      ],
      tracesSampleRate: 0.5,
      beforeBreadcrumb: (
        breadcrumb: Sentry.Breadcrumb,
        hint?: Record<string, string>,
      ): Sentry.Breadcrumb | null =>
        // This will filter out clicks/typing
        breadcrumb.category && breadcrumb.category.includes('ui.') ? null : breadcrumb,
      beforeSend: (
        event: Sentry.Event,
        hint?: Sentry.EventHint,
      ): PromiseLike<Sentry.Event | null> | Sentry.Event | null => {
        if (!this.sentryConfig.enable) return null;
        // @ts-ignore
        const httpStatus = event.extra?.__serialized__?.status;
        const isStatusValid =
          httpStatus === undefined || !this.filteredStatuses.includes(httpStatus);

        return isStatusValid ? event : null;
      },
      maxBreadcrumbs: 30,
    });

    /**
     * NgRx effects service requires ErrorService
     * Here we need currentAdmin but it is empty because CurrentAdminEffects is not initialized yet
     * setTimeout fixes this issue
     */
    setTimeout(() => {
      this.account.currentAdmin$.subscribe(currentAdmin =>
        Sentry.setUser({
          id: `${currentAdmin.id}`,
          email: currentAdmin.email,
          username: `${currentAdmin.firstName} ${currentAdmin.lastName}`,
          roleName: currentAdmin.roleName,
        }),
      );

      this.account.currentCompany$.subscribe(currentCompany =>
        Sentry.setExtra('Company', currentCompany?.id),
      );
    });
  }

  public handleError(err: any): void {
    try {
      if (this.sentryConfig.enable) {
        if (err instanceof HttpErrorResponse) {
          if (!this.filteredStatuses.includes(err.status)) {
            Sentry.captureMessage(this.getMessage(err));
          }
        } else {
          this.sendErrorToSentry(err);
        }
      }
    } finally {
      if (!this.sentryConfig.enable) {
        console.error(err);
      }
    }
  }

  public trace(name: string): void {
    try {
      throw new Error(name);
    } catch (e) {
      this.handleError(e);
    }
  }

  private getMessage(response: HttpErrorResponse): string {
    return `Http request failed. (${response.url}, ${response.status}, ${response.message})`;
  }

  private sendErrorToSentry(err: any): void {
    Sentry.setTags({ fatal: 'true' });
    Sentry.captureException(err.originalError || err);
  }

  private getTransactionName(context: TransactionContext): TransactionContext {
    if (context.op === 'pageload') {
      return { name: context.op };
    }

    const dialogOutletName = 'dialog';
    const { queryParams: nextQueryParams } = this.router.parseUrl(this.nextPath);
    if (nextQueryParams.candidate) {
      this.dialogActive = true;
      return {
        name: [
          '/candidate_dialog',
          nextQueryParams.tab || 'overview',
          nextQueryParams.openInterview === 'true' && 'open_interview',
        ]
          .filter(Boolean)
          .join('/'),
      };
    }

    const primaryOutlet = this.route.children.find(({ outlet }) => 'primary' === outlet) || null;
    const dialogOutlet = this.route.children.find(({ outlet }) => 'dialog' === outlet) || null;

    const primaryPath = '/' + this.getPathFromRoute(primaryOutlet);
    const dialogPath = this.getPathFromRoute(dialogOutlet);

    if (dialogPath) {
      this.dialogActive = true;
      return { name: `${dialogOutletName}:${dialogPath}` };
    }

    if (this.dialogActive) {
      this.dialogActive = false;
      return { name: primaryPath, sampled: false };
    }

    return { name: primaryPath };
  }

  private getPathFromRoute(activatedRoute: ActivatedRoute | null) {
    let route: ActivatedRoute | null = activatedRoute;
    let path: string[] = [];
    while (route) {
      const { routeConfig } = route;
      if (routeConfig && routeConfig.path) {
        path = [...path, ...routeConfig.path.split('/')];
      }
      route = route.firstChild;
    }
    return path.join('/');
  }
}
